import client from './client';

export enum PaymentType {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}

export enum PaymentMethod {
  CASH = 'CASH',
  CREDIT_CARD = 'CREDIT_CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CHECK = 'CHECK',
  MOBILE_PAYMENT = 'MOBILE_PAYMENT',
  OTHER = 'OTHER',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
}

export enum PaymentCategory {
  SALES = 'SALES',
  PURCHASE = 'PURCHASE',
  EXPENSE = 'EXPENSE',
  REFUND = 'REFUND',
  SERVICE = 'SERVICE',
  SUBSCRIPTION = 'SUBSCRIPTION',
  OTHER = 'OTHER',
}

export enum Currency {
  ZAR = 'ZAR',
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
}

export enum GatewayProvider {
  STRIPE = 'STRIPE',
  PAYPAL = 'PAYPAL',
  PAYFAST = 'PAYFAST',
  OZOW = 'OZOW',
  MANUAL = 'MANUAL',
}

export enum RecurringFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
}

export interface Payment {
  id: string;
  paymentNumber: string;
  paymentType: PaymentType;
  paymentMethod: PaymentMethod;
  amount: number;
  currency: Currency;
  status: PaymentStatus;
  paymentDate: string;
  processedDate?: string;
  description: string;
  reference?: string;
  invoiceNumber?: string;
  customerName?: string;
  customerEmail?: string;
  vendorName?: string;
  vendorEmail?: string;
  category: PaymentCategory;
  notes?: string;
  fees?: number;
  transactionId?: string;
  gatewayProvider?: GatewayProvider;
  receiptUrl?: string;
  isRecurring: boolean;
  recurringFrequency?: RecurringFrequency;
  nextDueDate?: string;
  accountId?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePaymentDto {
  paymentNumber?: string;
  paymentType: PaymentType;
  paymentMethod: PaymentMethod;
  amount: number;
  currency?: Currency;
  status?: PaymentStatus;
  paymentDate: string;
  processedDate?: string;
  description: string;
  reference?: string;
  invoiceNumber?: string;
  customerName?: string;
  customerEmail?: string;
  vendorName?: string;
  vendorEmail?: string;
  category: PaymentCategory;
  notes?: string;
  fees?: number;
  transactionId?: string;
  gatewayProvider?: GatewayProvider;
  receiptUrl?: string;
  isRecurring?: boolean;
  recurringFrequency?: RecurringFrequency;
  nextDueDate?: string;
  accountId?: string;
  metadata?: Record<string, any>;
}

export interface UpdatePaymentDto {
  paymentType?: PaymentType;
  paymentMethod?: PaymentMethod;
  amount?: number;
  currency?: Currency;
  status?: PaymentStatus;
  paymentDate?: string;
  processedDate?: string;
  description?: string;
  reference?: string;
  invoiceNumber?: string;
  customerName?: string;
  customerEmail?: string;
  vendorName?: string;
  vendorEmail?: string;
  category?: PaymentCategory;
  notes?: string;
  fees?: number;
  transactionId?: string;
  gatewayProvider?: GatewayProvider;
  receiptUrl?: string;
  isRecurring?: boolean;
  recurringFrequency?: RecurringFrequency;
  nextDueDate?: string;
  accountId?: string;
  metadata?: Record<string, any>;
}

export interface PaymentFilters {
  paymentType?: PaymentType;
  paymentMethod?: PaymentMethod;
  status?: PaymentStatus;
  category?: PaymentCategory;
  currency?: Currency;
  gatewayProvider?: GatewayProvider;
  customerName?: string;
  vendorName?: string;
  isRecurring?: boolean;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface PaymentAnalytics {
  totalIncoming: number;
  totalOutgoing: number;
  netFlow: number;
  totalPayments: number;
  pendingPayments: number;
  failedPayments: number;
  amountsByStatus: Record<string, number>;
  countsByStatus: Record<string, number>;
  amountsByMethod: Record<string, number>;
  amountsByCategory: Record<string, number>;
  totalFees: number;
}

// API Functions
export const getPayments = async (filters?: PaymentFilters): Promise<Payment[]> => {
  const params = new URLSearchParams();
  
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
  }
  
  const response = await client.get<Payment[] | { data: Payment[] }>(`/accounting/payments?${params.toString()}`);
  // Handle both direct array response and wrapped response
  return Array.isArray(response.data) ? response.data : (response.data as any).data || response.data;
};

export const getPayment = async (id: string): Promise<Payment> => {
  const response = await client.get<Payment | { data: Payment }>(`/accounting/payments/${id}`);
  // Handle both direct object response and wrapped response
  return (response.data as any).id ? response.data as Payment : (response.data as any).data || response.data;
};

export const createPayment = async (payment: CreatePaymentDto): Promise<Payment> => {
  const response = await client.post<Payment | { data: Payment }>('/accounting/payments', payment);
  // Handle both direct object response and wrapped response
  return (response.data as any).id ? response.data as Payment : (response.data as any).data || response.data;
};

export const updatePayment = async (id: string, payment: UpdatePaymentDto): Promise<Payment> => {
  const response = await client.put<Payment | { data: Payment }>(`/accounting/payments/${id}`, payment);
  // Handle both direct object response and wrapped response
  return (response.data as any).id ? response.data as Payment : (response.data as any).data || response.data;
};

export const deletePayment = async (id: string): Promise<void> => {
  await client.delete(`/accounting/payments/${id}`);
};

export const getPaymentAnalytics = async (filters?: PaymentFilters): Promise<PaymentAnalytics> => {
  const params = new URLSearchParams();
  
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
  }
  
  const response = await client.get<PaymentAnalytics | { data: PaymentAnalytics }>(`/accounting/payments/analytics?${params.toString()}`);
  // Handle both direct object response and wrapped response
  return (response.data as any).totalIncoming !== undefined ? response.data as PaymentAnalytics : (response.data as any).data || response.data;
};

export const processPayment = async (id: string): Promise<Payment> => {
  const response = await client.post<Payment | { data: Payment }>(`/accounting/payments/${id}/process`);
  // Handle both direct object response and wrapped response
  return (response.data as any).id ? response.data as Payment : (response.data as any).data || response.data;
};

export const completePayment = async (id: string): Promise<Payment> => {
  const response = await client.post<Payment | { data: Payment }>(`/accounting/payments/${id}/complete`);
  // Handle both direct object response and wrapped response
  return (response.data as any).id ? response.data as Payment : (response.data as any).data || response.data;
};

export const failPayment = async (id: string, reason?: string): Promise<Payment> => {
  const response = await client.post<Payment | { data: Payment }>(`/accounting/payments/${id}/fail`, { reason });
  // Handle both direct object response and wrapped response
  return (response.data as any).id ? response.data as Payment : (response.data as any).data || response.data;
};

// Display helpers
export const getPaymentTypeDisplay = (type: PaymentType): string => {
  switch (type) {
    case PaymentType.INCOMING:
      return 'Incoming';
    case PaymentType.OUTGOING:
      return 'Outgoing';
    default:
      return type;
  }
};

export const getPaymentMethodDisplay = (method: PaymentMethod): string => {
  switch (method) {
    case PaymentMethod.CASH:
      return 'Cash';
    case PaymentMethod.CREDIT_CARD:
      return 'Credit Card';
    case PaymentMethod.BANK_TRANSFER:
      return 'Bank Transfer';
    case PaymentMethod.CHECK:
      return 'Check';
    case PaymentMethod.MOBILE_PAYMENT:
      return 'Mobile Payment';
    case PaymentMethod.OTHER:
      return 'Other';
    default:
      return method;
  }
};

export const getPaymentStatusDisplay = (status: PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.PENDING:
      return 'Pending';
    case PaymentStatus.PROCESSING:
      return 'Processing';
    case PaymentStatus.COMPLETED:
      return 'Completed';
    case PaymentStatus.FAILED:
      return 'Failed';
    case PaymentStatus.CANCELLED:
      return 'Cancelled';
    case PaymentStatus.REFUNDED:
      return 'Refunded';
    default:
      return status;
  }
};

export const getPaymentCategoryDisplay = (category: PaymentCategory): string => {
  switch (category) {
    case PaymentCategory.SALES:
      return 'Sales';
    case PaymentCategory.PURCHASE:
      return 'Purchase';
    case PaymentCategory.EXPENSE:
      return 'Expense';
    case PaymentCategory.REFUND:
      return 'Refund';
    case PaymentCategory.SERVICE:
      return 'Service';
    case PaymentCategory.SUBSCRIPTION:
      return 'Subscription';
    case PaymentCategory.OTHER:
      return 'Other';
    default:
      return category;
  }
};

export const getCurrencyDisplay = (currency: Currency): string => {
  switch (currency) {
    case Currency.ZAR:
      return 'R';
    case Currency.USD:
      return '$';
    case Currency.EUR:
      return '€';
    case Currency.GBP:
      return '£';
    default:
      return currency;
  }
};

export const getGatewayProviderDisplay = (provider: GatewayProvider): string => {
  switch (provider) {
    case GatewayProvider.STRIPE:
      return 'Stripe';
    case GatewayProvider.PAYPAL:
      return 'PayPal';
    case GatewayProvider.PAYFAST:
      return 'PayFast';
    case GatewayProvider.OZOW:
      return 'Ozow';
    case GatewayProvider.MANUAL:
      return 'Manual';
    default:
      return provider;
  }
}; 